import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { MultiSelectFieldSettings, SearchOperatorsConfig, CrawfordDropDownComponent } from 'crawford-shell';
import { SearchFilterService } from '../services/search-filter.service';
import { AccordionService } from '../services/accordion.service';

@Component({
  selector: 'app-claim-insurer-search',
  templateUrl: './claim-insurer-search.component.html',
  styleUrls: ['./claim-insurer-search.component.scss']
})
export class ClaimInsurerSearchComponent implements OnInit {
  @Input() public group: FormGroup;
  @Input() public accordionGroupName: string;
  @Input() searchOperatorsConfig: SearchOperatorsConfig;

  @ViewChild('clientAccountSearchOperator') private clientAccountSearchOperator: CrawfordDropDownComponent;

  public controlsReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public brokerGroups;
  public clientAccounts;
  public insurerGroups;
  public readonly apiFieldSettings: MultiSelectFieldSettings = { value: 'code', text: 'displayText' };
  public filteredInsurers = [];
  public filteredInsurerLocations = [];
  public filteredBrokers = [];
  public filteredBrokerLocations = [];
  public clientAccountLabel: string;
  public searchOperators: any;

  public searchTypes = [
    { id: 1, name: 'Code' },
    { id: 2, name: 'Name' }
  ];

  constructor(
    private searchFilterService: SearchFilterService,
    private accordionService: AccordionService
  ) {
  }

  public loadFilterValues(claimSource: string): void {
    Observable.forkJoin(
      this.searchFilterService.getSearchFilterInsurers(claimSource),
      this.searchFilterService.getSearchFilterBrokers(claimSource)
    ).subscribe((results) => {
      this.insurerGroups = results[0];
      this.brokerGroups = results[1];
      this.controlsReady.next(true);
    });
  }

  public brokerGroupRemoved(event: any): void {
    this.group.get('brokers').reset();
    const removedBrokers = event.itemData.brokers.map(x => x.code);
    this.filteredBrokers = this.filteredBrokers.filter(broker => removedBrokers.indexOf(broker.code) === -1);
  }

  public brokerRemoved(event: any): void {
    this.group.get('brokerLocations').reset();
    const removedBrokerLocations = event.itemData.locations.map(x => x.hash);
    this.filteredBrokerLocations = this.filteredBrokerLocations.filter(location => removedBrokerLocations.indexOf(location.code) === -1);
  }

  public insurerGroupRemoved(event: any): void {
    this.group.get('insurers').reset();
    const removedInsurers = event.itemData.insurers.map(x => x.code);
    this.filteredInsurers = this.filteredInsurers.filter(insurer => removedInsurers.includes(insurer.code));
  }

  public insurerRemoved(event: any): void {
    this.group.get('insurerLocations').reset();
    const removedLocations = event.itemData.locations.map(x => x.hash);
    this.filteredInsurerLocations = this.filteredInsurerLocations.filter(location => removedLocations.includes(location.code));
  }

  public ngOnInit(): void {
    ['insurerGroups', 'insurers', 'insurerLocations', 'brokerGroups',
      'brokers', 'brokerLocations', 'clientAccount' ]
      .forEach((name) => this.accordionService.addFormControl(this.accordionGroupName, this.group, name));

    ['clientAccountSearchType', 'clientAccountSearchOperator'].forEach(name => this.group.addControl(name, new FormControl()));

    this.configureChangeListeners();

    this.group.get('clientAccountSearchType').setValue(1);

    if (this.searchOperatorsConfig) {
      this.configureSearchOperators(this.searchOperatorsConfig.operators, this.searchOperatorsConfig.defaultValue);
    }

    const claimSource: string = this.group.value['claimSource'];
    this.loadFilterValues(claimSource);
  }

  public checkHasComma(event: any): void {
    if (this.clientAccountSearchOperator) {
      if (event.clientAccount && event.clientAccount.indexOf(',') > -1) {
        this.clientAccountSearchOperator.setEnabled(false);
        this.setOperatorValue(0);
      } else {
        this.clientAccountSearchOperator.setEnabled(true);
      }
    }
  }

  public resetSearchOperatorControl(): void {
    this.clientAccountSearchOperator.setEnabled(true);
  }

  private configureSearchOperators(operators: any, defaultValue: any): void {
    if (operators) {
      this.searchOperators = operators;

      this.setOperatorValue(defaultValue);
    }
  }

  private setOperatorValue(defaultValue: any): void {
    if (!this.group.controls['clientAccountSearchOperator']) {
      this.group.addControl('clientAccountSearchOperator', new FormControl());
    }
    this.group.get('clientAccountSearchOperator').setValue(defaultValue);
  }

  private configureChangeListeners(): void {
    this.group.get('insurerGroups').valueChanges.subscribe(value => this.filterInsurers(value));
    this.group.get('insurers').valueChanges.subscribe(value => this.filterInsurerLocations(value));
    this.group.get('brokerGroups').valueChanges.subscribe(value => this.filterBrokers(value));
    this.group.get('brokers').valueChanges.subscribe(value => this.filterBrokerLocations(value));
    this.group.get('clientAccountSearchType').valueChanges.subscribe(value => this.clientAccountSearchTypeChanged(value));
  }

  private clientAccountSearchTypeChanged(value: any): void {
    if (value === 1) {
      this.clientAccountLabel = 'Code(s)';
    } else if (value === 2) {
      this.clientAccountLabel = 'Name(s)';
    }
  }

  private filterBrokers(selectedCodes: any[]): void {
    if (selectedCodes) {
      const initial = this.brokerGroups
                            .filter(x => selectedCodes.includes(x.code))
                            .map(x => x.brokers);

      this.filteredBrokers = [].concat.apply([], initial);
    } else {
      this.group.get('brokers').reset();
    }
  }

  private filterBrokerLocations(selectedCodes: any[]): void {
    if (selectedCodes) {
      const initial = this.filteredBrokers
                            .filter(x => selectedCodes.includes(x.code))
                            .map(x => x.locations.map(y => {
                              return {
                                code: y.hash,
                                name: y.name,
                                displayText: x.code + ' : ' + y.code + ' : ' + y.name
                              };
                            }));

      this.filteredBrokerLocations = [].concat.apply([], initial);
    } else {
      this.group.get('brokerLocations').reset();
    }
  }

  private filterInsurers(selectedCodes: any[]): void {
    if (selectedCodes) {
      const initial = this.insurerGroups
                            .filter(x => selectedCodes.includes(x.code))
                            .map(x => x.insurers);

      this.filteredInsurers = [].concat.apply([], initial);
    } else {
      this.group.get('insurers').reset();
    }
  }

  private filterInsurerLocations(selectedCodes): void {
    if (selectedCodes) {
      const initial = this.filteredInsurers
                            .filter(x => selectedCodes.includes(x.code))
                            .map(x => x.locations.map(y => {
                              return {
                                code: y.hash,
                                name: y.name,
                                displayText: x.code + ' : ' + y.code + ' : ' + y.name
                              };
                            }));

      this.filteredInsurerLocations = [].concat.apply([], initial);
    } else {
      this.group.get('insurerLocations').reset();
    }
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/UI/src/app/claim/claim-list/claim-insurer-search/claim-insurer-search.component.ts